/**
 * 実績画面 - Cookie に保存された実績の達成状況を表示
 */

import { useState, useEffect } from 'react';
import { GAME_REGISTRY } from '../config/gameRegistry';
import { ACHIEVEMENTS } from '../config/achievements';
import { loadAchievements, type AchievementCookie } from '../utils/achievementChecker';
import { useTranslations, interpolate } from '../i18n';
import { BASE_PATH } from '../config';

const MAP_OPTIONS = Object.entries(GAME_REGISTRY)
  .filter(([_, entry]) => !entry.isWIP)
  .map(([id, entry]) => ({
    id,
    apiMapType: entry.apiMapType,
    displayName: entry.displayName,
  }));

export function AchievementsScreen() {
  const t = useTranslations();
  const [selectedApiMapType, setSelectedApiMapType] = useState(
    MAP_OPTIONS[0]?.apiMapType || 'barbados',
  );
  const [achievementData, setAchievementData] = useState<AchievementCookie>({});

  useEffect(() => {
    setAchievementData(loadAchievements());
  }, []);

  const mapAchievements = ACHIEVEMENTS.filter(a => a.mapType === selectedApiMapType);
  const unlockedCount = mapAchievements.filter(
    a => achievementData[selectedApiMapType]?.[a.id]?.unlocked,
  ).length;

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="h-screen bg-background text-foreground p-4 md:p-6 flex flex-col">
      <div className="max-w-xl mx-auto w-full flex flex-col min-h-0 flex-1">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <h1 className="text-xl md:text-2xl font-bold">{t.achievements.title}</h1>
          <button
            className="btn-secondary px-3 py-1.5 text-sm"
            onClick={() => { window.location.href = BASE_PATH; }}
          >
            {t.achievements.back}
          </button>
        </div>

        {/* Cookie notice */}
        <div className="bg-muted/30 rounded-lg p-3 mb-4 text-xs text-muted-foreground flex-shrink-0">
          {t.achievements.cookieNotice}
        </div>

        {/* Map Selector */}
        <select
          className="w-full px-3 py-2 text-sm rounded border border-border bg-background text-foreground mb-4 flex-shrink-0"
          value={selectedApiMapType}
          onChange={(e) => setSelectedApiMapType(e.target.value)}
        >
          {MAP_OPTIONS.map((m) => (
            <option key={m.id} value={m.apiMapType}>
              {m.displayName}
            </option>
          ))}
        </select>

        {/* Achievement List - scrollable */}
        <div className="flex-1 min-h-0 overflow-y-auto space-y-3 pr-1">
          {mapAchievements.map((achievement) => {
            const status = achievementData[selectedApiMapType]?.[achievement.id];
            const unlocked = status?.unlocked || false;
            const name = t.achievements.names[achievement.id] || achievement.name;
            const description = t.achievements.descriptions[achievement.id] || achievement.description;

            return (
              <div
                key={achievement.id}
                className={`rounded-lg border p-3 flex items-start gap-3 ${
                  unlocked
                    ? 'border-success/40 bg-white'
                    : 'border-border bg-muted/20 opacity-60'
                }`}
              >
                <span className="text-2xl">{achievement.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{name}</div>
                  <div className="text-xs text-muted-foreground">{description}</div>
                  {unlocked && status ? (
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                      <span className="font-medium">{t.achievements.unlocked}</span>
                      <span>{formatDate(status.unlockedAt)}</span>
                    </div>
                  ) : (
                    <div className="mt-1.5 text-xs text-muted-foreground">
                      {t.achievements.locked}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Progress */}
        <div className="text-center mt-4 pb-2 text-sm text-muted-foreground flex-shrink-0">
          {interpolate(t.achievements.progress, {
            unlocked: unlockedCount,
            total: mapAchievements.length,
          })}
        </div>
      </div>
    </div>
  );
}
